/* eslint-disable react/jsx-no-bind */
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { nullableNumber } from 'shared-code/conversion'
import SliderScore from 'shared-components/ui/SliderScore'
import {
  BasicStyledComponent,
  DSComplexScore,
  DSMAQSOverall,
  DSMutableInitScore,
  DSMutableScore,
} from 'shared-definitions/types'
import ArrowShare from 'shared-svg/ArrowShare.svg'
import People from 'shared-svg/People.svg'
import Soundguys from 'shared-svg/Soundguys.svg'
import Linked from '../service/Linked'
import styles from './ReviewScores.module.css'
import ScoreRing from './ScoreRing'

interface ReviewScoresProps extends BasicStyledComponent {
  scores: DSComplexScore[]
  mdaqs: DSMAQSOverall | null
  onSumbitNewScore: (newVal: DSMutableInitScore) => Promise<boolean>
}

function makeKey(val: Pick<DSMutableScore, 'key' | 'postId'>): string {
  return `rev_score_${val.postId}_${val.key}`
}

const ReviewScores: React.VFC<ReviewScoresProps> = ({
  mdaqs,
  onSumbitNewScore,
  className,
  scores,
}) => {
  const [componentScores, setComponentScores] = useState(() =>
    scores.map(score => ({
      score,
      submitted: true,
    }))
  )
  useEffect(() => {
    setComponentScores(s =>
      s.map(i => {
        const localStored = localStorage.getItem(makeKey(i.score.userScore))
        return {
          score: {
            ...i.score,
            userScore: {
              ...i.score.userScore,
              val: i.score.userScore.val ?? nullableNumber(localStored),
            },
          },
          submitted: localStored !== null,
        }
      })
    )
  }, [])

  if (!scores.length) {
    return null
  }

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.title}>{'Rating Metric'}</div>
      <div className={styles.title}>
        <Soundguys className={styles.titleIcon} />
        <span className={styles.titleText}>{'Our Rating'}</span>
      </div>
      <div className={styles.title}>
        <People className={styles.titleIcon} />
        <span className={styles.titleText}>{'User Rating'}</span>
      </div>
      <div className={styles.splitter} />
      {componentScores.map((item, index) => (
        <React.Fragment
          key={`${index}${item.score.label}${item.score.ourScore}${item.score.userScore.val ?? ''}`}
        >
          <div className={styles.prop}>{item.score.label}</div>
          <div className={styles.ourScore}>{item.score.ourScore.toFixed(1)}</div>
          <SliderScore
            onSubmit={async newVal => {
              setComponentScores(s =>
                s.map((i, subIndex) => {
                  if (index === subIndex) {
                    return {
                      score: {
                        ...i.score,
                        userScore: {
                          ...i.score.userScore,
                          val: i.score.userScore.val ?? newVal,
                        },
                      },
                      submitted: true,
                    }
                  }
                  return i
                })
              )
              const result = await onSumbitNewScore({ ...item.score.userScore, val: newVal })
              setComponentScores(s =>
                s.map((i, subIndex) => ({
                  ...i,
                  submitted: index === subIndex ? result : i.submitted,
                }))
              )

              if (result) {
                localStorage.setItem(makeKey(item.score.userScore), JSON.stringify(newVal))
              }
            }}
            submitted={item.submitted}
            className={styles.userScore}
            score={item.score.userScore.val}
          />
        </React.Fragment>
      ))}
      {mdaqs && (
        <React.Fragment>
          <div className={styles.mdaqsHead}>
            <div className={styles.title}>{mdaqs.title}</div>
            <Linked {...mdaqs.link.pLink} className={styles.mdaqsLink}>
              {mdaqs.link.label}
              <ArrowShare className={styles.mdaqsLinkIcon} />
            </Linked>
          </div>
          <div className={styles.splitter} />
          <div className={styles.mdaqsScores}>
            {mdaqs.scores.map((item, index) => (
              <div key={index} className={styles.mdaqsScore}>
                <div className={styles.mdaqsScoreTitle}>{item.name}</div>
                <ScoreRing
                  maxScore={mdaqs.maxValue - 1}
                  className={styles.mdaqsScoreRing}
                  score={item.value - 1}
                  variant="base"
                >
                  {item.value}
                </ScoreRing>
              </div>
            ))}
          </div>
        </React.Fragment>
      )}
    </div>
  )
}

export default ReviewScores
